<script setup lang="ts">
import "swiper/css";

import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/vue/20/solid";
import type { Swiper as SwiperInstance } from "swiper/types";
import { Swiper, SwiperSlide } from "swiper/vue";
import { twMerge } from "tailwind-merge";
import { computed, ref } from "vue";

import { useUser } from "@/composables/api/useUser";
import { rewards } from "@/rewards";

import GiftProduct from "./GiftProduct.vue";

const swiperInstance = ref<SwiperInstance | null>(null);
const activeIndex = ref(0);
const { data: user } = useUser();

const currentRewards = computed(() => {
  if (!user.value?.current_mechanic) {
    return [];
  }
  const mechanic = user.value.current_mechanic;
  return rewards[mechanic];
});
</script>

<template>
  <div class="w-[100vw] md:w-auto">
    <Swiper
      slidesPerView="auto"
      :centeredSlides="true"
      class="relative h-full w-full !pt-6"
      @slideChange="activeIndex = $event.activeIndex"
      @swiper="swiperInstance = $event"
    >
      <SwiperSlide
        v-for="reward in currentRewards"
        :key="reward.name"
        class="relative mx-2.5 !flex !h-[unset] !w-50 items-stretch rounded-[20px] bg-white"
      >
        <GiftProduct :reward="reward" />
      </SwiperSlide>
    </Swiper>
  </div>

  <div class="mt-4 flex items-center gap-4" role="region" aria-label="Carrousel des cadeaux">
    <button
      type="button"
      :disabled="activeIndex === 0"
      :aria-disabled="activeIndex === 0"
      aria-label="Diapositive précédente"
      :class="
        twMerge(
          'text-galbani disabled:text-galbani/20 cursor-pointer transition-opacity duration-300 ease-out',
          user?.current_mechanic === 'lactel' && 'text-lactel-blue',
        )
      "
      @click="swiperInstance?.slidePrev()"
    >
      <ChevronLeftIcon class="size-5" aria-hidden="true" />
    </button>

    <div class="flex gap-2.5" role="tablist" aria-label="Points de navigation du carrousel">
      <button
        v-for="(reward, index) in currentRewards"
        :key="reward.name"
        type="button"
        role="tab"
        class="cursor-pointer"
        :aria-selected="swiperInstance?.activeIndex === index"
        :aria-label="`Diapositive ${index + 1} sur ${Object.keys(reward).length}: ${$t(reward.name)}`"
        @click="swiperInstance?.slideTo(index)"
      >
        <span
          role="presentation"
          class="bg-galbani block rounded-full transition-all duration-300 ease-out"
          :class="{
            'bg-galbani/20 size-2': swiperInstance?.activeIndex !== index,
            'bg-galbani size-3': swiperInstance?.activeIndex === index,
          }"
        />
      </button>
    </div>

    <button
      type="button"
      :disabled="activeIndex >= currentRewards.length - 1"
      :aria-disabled="activeIndex >= currentRewards.length - 1"
      aria-label="Diapositive suivante"
      :class="
        twMerge(
          'text-galbani disabled:text-galbani/20 cursor-pointer transition-opacity duration-300 ease-out',
          user?.current_mechanic === 'lactel' && 'text-lactel-blue',
        )
      "
      @click="swiperInstance?.slideNext()"
    >
      <ChevronRightIcon class="size-5" aria-hidden="true" />
    </button>
  </div>
</template>
